<template>
  <div>
    <v-navigation-drawer
      app
      class="main-navigation-menu px-0 py-3"
      data-tourid="main-manu-bar"
      data-testid="main-navigationDrawer"
      width="256"
      style="overflow: visible;"
      color="blue-grey darken-3"
      :mini-variant="mini"
      :mini-variant-width="88"
      :value="visible"
      @input="checkChange"
    >
      <template v-slot:prepend>
        <v-list class="px-0 relative">
          <Button
            fab
            class="mini-mode-button hidden-md-and-down"
            color="white"
            elevation="2"
            size="x-small"
            :icon="mini ? 'mdi-chevron-right' : 'mdi-chevron-left'"
            @click="toggleMiniMode"
          />
          <v-list-item
            v-if="shouldShowLogo"
            class="d-flex"
            :class="{
              'pl-7': !mini,
              'justify-center': mini,
            }"
          >
            <router-link to="/" class="d-flex align-center">
              <Logo :isotype="mini" type="white" />
            </router-link>
          </v-list-item>

          <v-list-item
            class="nav-item px-7 mt-5 mb-4 d-flex cursor-pointer"
            data-tourid="create-new-button"
            @click="showCreateModal = true"
          >
            <CreateButton gradient />
            <p
              class="font-size-14 font-weight-medium mb-0 ml-4 white--text"
              style="letter-spacing: 0.75px;"
            >
              {{ $t('common.button.create') }}
            </p>
          </v-list-item>
        </v-list>
      </template>

      <v-list class="px-0" dense nav>
        <template v-for="item in navigationItems">
          <v-list-group
            v-if="item.subItems && item.subItems.length"
            class="menu-item tree"
            active-class="rounded-0 node"
            color="blue-grey darken-2"
            no-action
            append-icon=""
            :value="true"
            :ripple="false"
            :key="item.title"
          >
            <template v-slot:activator>
              <NavigationItem
                menu
                :mini="mini"
                :href="item.href"
                :tourId="item.tourId"
                :title="item.title"
                :activeIcon="item.activeIcon"
                :inactiveIcon="item.inactiveIcon"
                :badgeText="item.badgeText"
                :badgeTooltip="item.badgeTooltip"
                :badgeColor="item.badgeColor"
              />
            </template>

            <NavigationItem
              v-for="subItem in item.subItems"
              isSubItem
              :key="subItem.href"
              :href="subItem.href"
              :tourId="subItem.tourId"
              :title="subItem.title"
              :mini="mini"
              :activeIcon="subItem.activeIcon"
              :inactiveIcon="subItem.inactiveIcon"
              :badgeText="subItem.badgeText"
              :badgeTooltip="subItem.badgeTooltip"
              :badgeColor="subItem.badgeColor"
            />
          </v-list-group>
          <NavigationItem
            v-else
            :key="item.href"
            :href="item.href"
            :tourId="item.tourId"
            :title="item.title"
            :mini="mini"
            :activeIcon="item.activeIcon"
            :inactiveIcon="item.inactiveIcon"
            :query="item.query"
            :badgeText="item.badgeText"
            :badgeTooltip="item.badgeTooltip"
            :badgeColor="item.badgeColor"
          />
        </template>
      </v-list>

      <template v-slot:append>
        <div
          class="d-flex justify-center mb-4 px-6"
          v-if="aiFeaturesAreEnabled"
        >
          <ReverAIButtonNavigationDrawer
            :mini="mini"
            @click="goToReverAIChat"
          />
        </div>
        <NavigationItem
          v-else
          key="help-center"
          inactiveIcon="mdi-help-circle-outline"
          activeIcon="mdi-help-circle-outline"
          external
          :href="$constants.urls.helpCenter"
          :title="$t('menu.header.help')"
          :mini="mini"
        />
      </template>
    </v-navigation-drawer>
    <CreateContentModal
      v-if="showCreateModal"
      :show="showCreateModal"
      :replicateRevData="replicateRevData"
      @close="closeCreateModal"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import { mapGetters } from 'vuex'

import access from '@/lib/access'
import eventHub, { HUB_EVENTS } from '@/lib/event-hub'
import trackingMixin from '@/mixins/tracking/trackerMixin.vue'
import mixin from '@/mixins/mixin.vue'

import Logo from '@/components/OrganizationLogo.vue'
import Button from '@/components/UI/Button'
import CreateButton from '@/components/UI/CreateButton'
import CreateContentModal from '@/components/CreateContentModal/CreateContentModal.vue'
import NavigationItem from './NavigationItem.vue'
import ReverAIButtonNavigationDrawer from '@/components/ReverAIButtonNavigationDrawer.vue'

export default {
  name: 'NavigationDrawer',

  mixins: [trackingMixin, mixin],

  components: {
    Logo,
    Button,
    CreateButton,
    CreateContentModal,
    NavigationItem,
    ReverAIButtonNavigationDrawer,
  },

  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      mini: false,
      showCreateModal: false,
      replicateRevData: null,
    }
  },

  async mounted() {
    eventHub.$on(HUB_EVENTS.REV_VIEW.REPLICATE_REV, this.replicateRev)
  },

  beforeDestroy() {
    eventHub.$off(HUB_EVENTS.REV_VIEW.REPLICATE_REV, this.replicateRev)
    this.replicateRevData = null
  },

  methods: {
    accessAllowed(acl) {
      return access.aclsAllowedByPlan(this.organization, acl)
    },

    hasFeatureEnabled(feature) {
      return this.organization[`featuresEnabled__${feature}`]
    },

    checkChange(isVisible) {
      if (!isVisible) this.$emit('hide')
    },

    toggleMiniMode() {
      this.trackHomeCustomize({ section: 'mainNavigation' })
      this.mini = !this.mini
    },

    goToReverAIChat() {
      this.navigateTo('/rever-ai-chat')
    },

    replicateRev(revData) {
      this.replicateRevData = revData
      this.showCreateModal = true
    },

    closeCreateModal() {
      this.showCreateModal = false
      this.replicateRevData = null
    },
  },

  computed: {
    ...mapGetters(['account', 'organization', 'siteId']),

    aiFeaturesAreEnabled() {
      return access.aiFeaturesAreEnabled(this.account, this.organization)
    },

    canAccessValidationsView() {
      return this.isLeadershipOrCoachOrFinancialCoach
    },

    shouldShowLogo() {
      const hasCustomLogo = Boolean(this.organization.logo)
      const miniCustomLogo = hasCustomLogo && this.mini
      return !miniCustomLogo
    },

    isLeadershipOrCoachOrFinancialCoach() {
      return access.isLeadershipOrCoachOrFinancialCoach(this.account)
    },

    isLeadershipOrFinancialCoach() {
      return access.isLeadershipOrFinancialCoach(this.account)
    },

    navigationItems() {
      return _.compact([
        {
          href: '/home',
          title: this.$t('common.home'),
          activeIcon: 'mdi-home',
          inactiveIcon: 'mdi-home-outline',
          tourId: 'home-section-menu',
        },
        {
          query: {
            sitesOption: true,
            typesOption: true,
            statusOption: true,
            roleOption: true,
          },
          href: '/explore',
          title: this.$t('explore.explore'),
          activeIcon: 'mdi-home-search',
          inactiveIcon: 'mdi-home-search-outline',
          tourId: 'explore-section-menu',
        },

        this.hasFeatureEnabled('boards') && {
          href: '/boards',
          title: this.$t('boards.board_other'),
          activeIcon: 'mdi-view-column',
          inactiveIcon: 'mdi-view-column-outline',
          tourId: 'boards-section-menu',
        },

        this.hasFeatureEnabled('routines') && {
          href: '/routine-definitions',
          title: this.$t('routines.routine_other'),
          activeIcon: 'mdi-clipboard-text',
          inactiveIcon: 'mdi-clipboard-text-outline',
        },

        this.hasFeatureEnabled('tasks') && {
          query: {
            assignees: [this.$store.getters.accountId],
            status: [
              this.$constants.tasksStatus.open,
              this.$constants.tasksStatus.inProgress,
            ],
            type: 'assignedToMe',
          },
          href: '/tasks',
          title: this.$t('tasks.task_other'),
          activeIcon: 'mdi-check',
          inactiveIcon: 'mdi-check',
          tourId: 'tasks-section-menu',
        },
        this.accessAllowed('insights') &&
          this.hasFeatureEnabled('insights') && {
            href: '/insights/dashboard',
            title: this.$t('insights.insights'),
            activeIcon: 'mdi-chart-box',
            inactiveIcon: 'mdi-chart-box-outline',
            id: 'insights',
            subItems: [
              {
                title: this.$t('insights.dashboard'),
                href: '/insights/dashboard',
                tourId: 'dashboard-section-menu',
              },
              {
                title: this.$t('insights.report_other'),
                href: '/insights/reports',
              },
            ],
          },
        {
          href: '/directory',
          title: this.$t('common.directory'),
          activeIcon: 'mdi-account-box',
          inactiveIcon: 'mdi-account-box-outline',
        },
        {
          href: '/teams',
          title: this.$t('common.team_other'),
          activeIcon: 'mdi-account-group',
          inactiveIcon: 'mdi-account-group-outline',
        },
        {
          href: '/recognitions',
          title: this.$t('recognitions.recognition_other'),
          activeIcon: 'mdi-medal',
          inactiveIcon: 'mdi-medal-outline',
        },
        this.accessAllowed('validations') &&
          this.canAccessValidationsView && {
            tourId: 'validations-section-menu',
            href: '/validations',
            title: this.$t('rev.validation.validation_other'),
            activeIcon: 'mdi-check-decagram',
            inactiveIcon: 'mdi-check-decagram-outline',
          },
      ])
    },
  },
}
</script>

<style lang="scss">
.main-navigation-menu {
  box-shadow: $elevation;

  .mini-mode-button {
    transition: 100ms opacity;
    position: absolute;
    top: 4px;
    right: -16px;
    z-index: 7;
    opacity: 0;
  }

  &:hover {
    .mini-mode-button {
      opacity: 1;
    }
  }

  .create-text {
    font-size: 18px !important;
    letter-spacing: 0.44px !important;
  }

  .nav-item:hover {
    background: $color-blue-grey-darken2;
  }

  .v-list-group__header {
    padding: 0 !important;
  }

  .menu-item {
    .v-list-item--link:hover {
      background: $color-blue-grey-darken2;
    }
  }

  .tree {
    margin: 0;
    position: relative;
  }
  .tree .node {
    overflow: visible;
  }
  .tree .v-list-group__items {
    padding-left: 24px;
    margin-bottom: 12px;
    position: relative;
    overflow: hidden;
  }
  .tree .v-list-group__items .node {
    position: relative;
  }
  .tree .v-list-group__items .node::before {
    position: absolute;
    left: -21px;
    top: 0px;
    width: 20px;
    height: 20px;
    border-left: 1px solid $color-blue-grey-lighten1;
    border-bottom: 1px solid $color-blue-grey-lighten1;
    content: '';
    opacity: 1;
    border-radius: 0 0 0 6px;
    background: transparent;
  }
  .tree .v-list-group__items .node::after {
    position: absolute;
    left: -21px;
    bottom: 26px;
    width: 20px;
    height: 100px;
    border-left: 1px solid $color-blue-grey-lighten1;
    content: '';
    opacity: 1;
    background: transparent;
  }
}
</style>
